import React, { useEffect, useState } from 'react';
import { Bell, X, Check, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationDropdown = ({ notifications = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState(notifications);

  useEffect(() => {
    setItems(notifications);
  }, [notifications]);

  const unreadCount = items.filter((n) => !n.read).length;

  const removeNotification = (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  const markAsRead = (id) => {
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative inline-flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl sm:rounded-2xl border border-[#dbcfb7] bg-[#fff9f1] text-[#4d3d2c] shadow-sm transition hover:bg-[#f4e5cc]"
        aria-label="Open notifications"
      >
        <Bell size={18} className="sm:w-5 sm:h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center border-2 border-white shadow">
            {unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-xs sm:bg-transparent"
            />

            {/* Notification Drawer / Container */}
            {/* On Mobile: fixed top-16 left-0 right-0 z-50 w-full max-w-full px-3 */}
            {/* On Desktop/Tablet: absolute right-0 top-full mt-2 w-80 */}
            <motion.div
              initial={{ opacity: 0, y: -12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="fixed top-16 sm:top-auto left-0 right-0 sm:left-auto sm:right-0 sm:absolute sm:mt-2 z-50 w-full sm:w-85 px-3 sm:px-0"
            >
              <div className="bg-white dark:bg-[#241b14] rounded-2xl shadow-2xl border border-[#e7dbc8] dark:border-[#3e2f20] overflow-hidden flex flex-col max-h-[82vh] sm:max-h-[460px] w-full">
                
                {/* Header with Title & Close (X) Icon */}
                <div className="p-3.5 sm:p-4 border-b border-[#e7dbc8]/60 dark:border-[#3e2f20] flex items-center justify-between bg-[#fff9f1] dark:bg-[#1c1510]">
                  <div className="flex items-center gap-2">
                    <Bell size={18} className="text-[#E8B530]" />
                    <h3 className="font-bold text-sm sm:text-base text-[#2c200f] dark:text-[#f8efe5]">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="text-[11px] bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded-full font-bold">
                        {unreadCount} new
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-[11px] font-semibold text-[#8b6f53] dark:text-[#d8c3aa] hover:text-[#2c200f] underline"
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-1.5 rounded-xl text-gray-500 hover:text-gray-900 dark:text-[#d8c3aa] dark:hover:text-white hover:bg-black/5 dark:hover:bg-[#3e2f20] transition"
                      aria-label="Close notifications"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Notifications List */}
                <div className="divide-y divide-[#e7dbc8]/40 dark:divide-[#3e2f20] overflow-y-auto max-h-[68vh] sm:max-h-[380px] scrollbar-thin">
                  {items.length > 0 ? (
                    items.map((notification) => (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => markAsRead(notification.id)}
                        className={`p-3.5 sm:p-4 cursor-pointer transition ${
                          !notification.read
                            ? 'bg-[#fff5e8] dark:bg-[#2e2218]'
                            : 'hover:bg-[#fff9f1] dark:hover:bg-[#1c1510]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {!notification.read && (
                                <span className="w-2 h-2 rounded-full bg-[#E8B530] flex-shrink-0" />
                              )}
                              <p className="text-xs sm:text-sm font-semibold text-[#2c200f] dark:text-[#f8efe5] truncate">
                                {notification.title}
                              </p>
                            </div>
                            <p className="text-xs text-[#7e6d54] dark:text-[#d8c3aa] mt-1 leading-snug">
                              {notification.message}
                            </p>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 font-medium">
                              {notification.time}
                            </p>
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notification.id);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition"
                            title="Remove notification"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-xs sm:text-sm text-[#7e6d54] dark:text-[#d8c3aa]">
                      No notifications
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationDropdown;
